import { useEffect, useState } from "react";
import { PixCopiaEColaProps, PixModalProps } from "../Types";
import { FaCheck, FaCopy, FaSpinner } from "react-icons/fa";
import { formatCurrency } from "../Utils";
import { X } from "lucide-react";
import { FaPix } from "react-icons/fa6";
import QRCode from 'react-qr-code';

const useCopyToClipboard = () => {
    const [isCopied, setIsCopied] = useState(false);
    const handleCopy = (text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    };
    return { isCopied, handleCopy };
};

export const PixModal: React.FC<PixModalProps> = ({ 
    isOpen, 
    onClose, 
    pixData, 
    finalValue, 
    transactionId,
    streamUrl,
}) => {
    const [paymentStatus, setPaymentStatus] = useState<'AGUARDANDO' | 'APROVADO'>('AGUARDANDO');

    useEffect(() => {
        const url = streamUrl?.trim();
        if (!isOpen || !url) return;

        const sseUrl = url.startsWith('http') ? url : `https://${url}`;
        console.log('[PIX SSE] Conectando:', sseUrl);
        const eventSource = new EventSource(sseUrl);

        const handlePayload = (raw: string) => {
            try {
                const data = JSON.parse(raw);
                const successStatus = ['APROVADO', 'PAGO', 'DEPOSITADO'];
                const byStatus = data.status && successStatus.includes(String(data.status).toUpperCase());
                const byPago = data.pago === true;
                if (byPago || byStatus) {
                    setPaymentStatus('APROVADO');
                    eventSource.close();
                }
            } catch (_) {
                // ignora payload inválido
            }
        };

        eventSource.onopen = () => {
            console.log('[PIX SSE] Conexão aberta');
        };

        eventSource.onmessage = (event) => {
            console.log('[PIX SSE] Evento (message):', event.data);
            handlePayload(event.data);
        };

        const eventTypes = ['payment', 'pago', 'status', 'payment_status'];
        eventTypes.forEach((type) => {
            eventSource.addEventListener(type, (event: MessageEvent) => {
                console.log(`[PIX SSE] Evento (${type}):`, event.data);
                handlePayload(event.data);
            });
        });

        eventSource.onerror = (e) => {
            console.warn('[PIX SSE] Erro ou conexão fechada', eventSource.readyState);
            eventSource.close();
        };

        return () => {
            eventSource.close();
        };
    }, [isOpen, streamUrl]);

    if (!isOpen || !pixData) return null;

    if (paymentStatus === 'APROVADO') {
        return (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 animate-fade-in">
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 relative flex flex-col max-h-[90vh] overflow-y-auto">
                    <div className="w-20 h-20 bg-emerald-100 rounded-full mx-auto flex items-center justify-center border-4 border-emerald-200">
                       <FaCheck className="w-10 h-10 text-emerald-500" />
                    </div>
                    <h2 className="text-2xl font-extrabold text-gray-800 text-center mt-6">Pagamento Aprovado!</h2>
                    <p className="text-gray-600 mt-2">
                        Seu pagamento de <span className="font-bold text-emerald-600">{formatCurrency(finalValue)}</span> foi confirmado com sucesso.
                    </p>
                    <button
                        onClick={onClose}
                        className="mt-8 w-full bg-sky-500 text-white py-3 px-4 rounded-xl hover:bg-sky-600 transition-all font-bold"
                    >
                        Fechar
                    </button>
                </div>
            </div>
        );
    }

    return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4 animate-fade-in">
        <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg p-6 sm:p-8 relative max-h-[90vh] overflow-y-auto">
        {/* Botão Fechar */}
        <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-full p-2 transition"
        >
            <X size={22} />
        </button>

        {/* Header com ícone */}
        <div className="flex flex-col items-center mb-4 sm:mb-6">
            <div className="flex flex-row items-center gap-4">
                <div className="bg-gradient-to-r from-sky-500 to-sky-600 text-white p-2 rounded-xl shadow-lg">
                    <FaPix className="h-4 w-4" />
                </div>
                <h2 className="text-2xl font-extrabold text-gray-800">Pague com PIX</h2>
            </div>
            <p className="text-gray-600 text-sm mt-2">
            Para pagar{" "}
            <span className="font-bold text-sky-600">
                {formatCurrency(finalValue)}
            </span>, escaneie o QR Code ou use o Pix Copia e Cola.
            </p>
        </div>

        {/* QR Code em destaque — usa copia e cola para o app do banco reconhecer o PIX */}
        <div className="flex justify-center">
            <div className="mt-6 mb-2 p-6 border-2 border-sky-300 rounded-2xl bg-sky-50/60 shadow-inner">
                {(pixData?.copiaECola || pixData?.qrCode) && (
                    <QRCode value={pixData.copiaECola || pixData.qrCode} size={180} />
                )}
            </div>
        </div>

        {/* Mensagem de Segurança */}
        <div className="mt-2 mb-2 bg-gradient-to-r from-sky-50 to-emerald-50 border border-sky-200 rounded-xl p-4">
            <div className="flex items-center gap-3">
                <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-sky-500 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
                        </svg>
                    </div>
                </div>
                <div className="flex-1">
                    <p className="text-sm text-sky-800 font-medium leading-relaxed">
                        Este pagamento será processado com segurança pela{" "}
                        <span className="font-bold">Chronos Informatica Ltda</span>, nossa plataforma 
                        certificada e protegida.
                    </p>
                </div>
            </div>
        </div>

        {/* Copia e Cola */}
        <PixCopiaECola codigo={pixData.copiaECola} />
        
        <div className="mt-6 flex items-center justify-center gap-3 text-sky-600 bg-sky-50 p-3 rounded-xl">
            <FaSpinner className="animate-spin h-5 w-5" />
            <span className="font-medium text-sm">Aguardando confirmação do pagamento...</span>
        </div>

        {/* Instruções */}
        <div className="mt-6 sm:mt-8 space-y-4">
            {[
            "Copie o código de pagamento no botão acima.",
            "Abra o app do banco e vá até a área de Pix. Escolha a opção \"Copia e Cola\".",
            "No campo \"Código Pix\", cole o código copiado no primeiro passo.",
            "Confirme os dados da transação e finalize o pagamento.",
            ].map((texto, i) => (
                <div key={i} className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-full bg-sky-500 text-white text-sm font-bold shadow leading-none">
                        {i + 1}
                    </div>
                    <p className="text-gray-700 text-sm leading-relaxed">{texto}</p>
                </div>
            ))}
        </div>
        </div>
    </div>
    );
};

// --- COMPONENTE DO "COPIA E COLA" ---
export const PixCopiaECola: React.FC<PixCopiaEColaProps> = ({ codigo }) => {
    const { isCopied, handleCopy } = useCopyToClipboard();
    return (
        <div>
            <label className="text-xs font-semibold text-gray-600 block mb-2">Pix Copia e Cola</label>
            <div className="relative">
                <input
                    type="text"
                    readOnly
                    value={codigo}
                    className="w-full bg-gradient-to-r from-sky-50 to-blue-50 border-2 border-sky-200 rounded-xl p-3 pr-12 text-gray-700 text-xs truncate focus:outline-none focus:border-sky-400 transition-all"
                />
                <button
                    onClick={() => handleCopy(codigo)}
                    className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-all ${
                        isCopied 
                            ? 'bg-emerald-100 text-emerald-600' 
                            : 'bg-sky-100 hover:bg-sky-200 text-sky-600 hover:text-sky-700'
                    }`}
                    title={isCopied ? "Copiado!" : "Copiar código"}
                >
                    {isCopied ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
                </button>
            </div>
            {isCopied && (
                <p className="text-xs text-emerald-600 font-medium mt-2 animate-fade-in">
                    ✓ Código copiado com sucesso!
                </p>
            )}
        </div>
    );
};